import React, { useState } from 'react'
import styled from 'styled-components'
import { IconHide, IconShow } from '../../assets/icons'
import { commonColors } from '@linqpal/common-frontend/src/theme'

interface PasswordInputProps {
  value: string
  label?: string
  height?: string
  width?: string
  hint?: string | React.ReactElement | null
  validationError?: boolean
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  mandatory?: boolean
}

export const PasswordInput = ({
  value,
  label,
  validationError,
  hint = null,
  height,
  width,
  onFocus,
  onBlur,
  onChange,
  mandatory,
}: PasswordInputProps) => {
  const [isShown, setIsShown] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  const togglePasswordVisibility = () => {
    setIsShown((prevState) => !prevState)
  }

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true)
    onFocus && onFocus(e)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)
    onBlur && onBlur(e)
  }

  return (
    <>
      <StyledInputLabel error={validationError}>
        {label || ''}
        {mandatory && <span>*</span>}
      </StyledInputLabel>
      <StyledInputWrapper
        isFocused={isFocused}
        height={height}
        width={width}
        error={validationError}
      >
        <StyledInputComponent
          value={value}
          error={validationError}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          type={isShown ? 'text' : 'password'}
        />
        {!!value?.length && (
          <ToggleVisibilityButton onClick={togglePasswordVisibility}>
            {isShown ? (
              <IconHide width={24} height={24} />
            ) : (
              <IconShow width={24} height={24} />
            )}
          </ToggleVisibilityButton>
        )}
      </StyledInputWrapper>
      {hint && <div>{hint}</div>}
    </>
  )
}

// Styled-components for the PasswordInput
const StyledInputWrapper = styled.div<{
  isFocused: boolean
  height?: string
  width?: string
  error?: boolean
}>`
  position: relative;
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || 'auto'};
  border: ${({ isFocused, error }) =>
    error
      ? '3px solid #EC002A'
      : isFocused
      ? `1px solid ${commonColors.accentText}`
      : `1px solid #99ADBA`};
  border-radius: 4px;
  height: ${({ height }) => height || '40px'};
  width: ${({ width }) => width};
  max-width: '100%';
`

const StyledInputComponent = styled.input<{
  error?: boolean
}>`
  font-size: 14px;
  line-height: 16px;
  font-weight: 600;
  padding: 10px 14px;
  color: #001929;
  border-radius: 4px;
  flex: 1;
  box-sizing: border-box;
  padding-horizontal: 14px;
  width: 100%;
  background-color: ${({ error }) => (error ? `#FFFFFF` : `#FFFFFF`)};
  padding-right: 44px; /* Room for the icon */
  outline: none;
  border: none;
  height: 100%;

  /* Hide the default browser password visibility icon */
  &::-ms-reveal,
  &::-ms-clear,
  &::-webkit-password-field {
    display: none;
  }

  /* Safari fixes */
  &::-webkit-contacts-auto-fill-button,
  &::-webkit-credentials-auto-fill-button {
    display: none;
  }

  /* Firefox fixes (password reveal icon) */
  &::-moz-password-reveal-button {
    display: none;
  }
`

const StyledInputLabel = styled.label<{ error?: boolean }>`
  color: ${({ error }) => (error ? `#EC002A` : `#001929`)};
  font-size: 12px;
  font-family: Inter;
  font-weight: 400;
  line-height: 16px;
  margin-bottom: 4px;
`

const ToggleVisibilityButton = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  margin-top: 1px;
  transform: translateY(-50%);
  background-color: transparent;
  border: none;
  cursor: pointer;
`
